import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const careersSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Careers at Fluffy Pets - Join Our Team',
    url: `${SITE_URL}/careers`,
    description:
      'Join the Fluffy Pets team! We are hiring pet groomers, veterinary technicians, trainers, and customer service staff in San Francisco.',
    isPartOf: { '@type': 'WebSite', name: 'Fluffy Pets', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: 'Senior Pet Groomer',
    datePosted: '2026-03-01',
    validThrough: '2026-06-01',
    description: 'We are looking for an experienced groomer who loves working with all breeds and sizes. Must have 3+ years of professional grooming experience.',
    employmentType: 'FULL_TIME',
    hiringOrganization: { '@type': 'Organization', name: 'Fluffy Pets', url: SITE_URL },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', streetAddress: '123 Pet Care Lane', addressLocality: 'San Francisco', addressRegion: 'CA', postalCode: '94102', addressCountry: 'US' },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: 'Veterinary Technician',
    datePosted: '2026-03-01',
    validThrough: '2026-06-01',
    description: 'Join our veterinary team to provide exceptional care for our boarding and daycare guests. CVT certification required.',
    employmentType: 'FULL_TIME',
    hiringOrganization: { '@type': 'Organization', name: 'Fluffy Pets', url: SITE_URL },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', streetAddress: '123 Pet Care Lane', addressLocality: 'San Francisco', addressRegion: 'CA', postalCode: '94102', addressCountry: 'US' },
    },
  },
];

// ... existing code ...

export default function CareersPage() {
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const departments = ['All', 'Grooming', 'Veterinary', 'Customer Service', 'Management'];

  const benefits = [
    { icon: 'ri-heart-line', title: 'Health Insurance', description: 'Comprehensive medical, dental, and vision coverage for you and your family' },
    { icon: 'ri-time-line', title: 'Flexible Hours', description: 'Work-life balance with flexible scheduling options' },
    { icon: 'ri-graduation-cap-line', title: 'Training & Development', description: 'Continuous learning opportunities and professional certifications' },
    { icon: 'ri-gift-line', title: 'Employee Discounts', description: 'Special discounts on all our pet care services and products' },
    { icon: 'ri-team-line', title: 'Great Team Culture', description: 'Work with passionate pet lovers in a supportive environment' },
    { icon: 'ri-calendar-check-line', title: 'Paid Time Off', description: 'Generous vacation days, sick leave, and paid holidays' }
  ];

  const openPositions = [
    { id: 1, title: 'Senior Pet Groomer', department: 'Grooming', location: 'Main Location', type: 'Full-time',
      description: "We're looking for an experienced groomer who loves working with all breeds and sizes. Must have 3+ years of professional grooming experience.",
      requirements: ['3+ years grooming experience', 'Certification preferred', 'Excellent customer service', 'Knowledge of breed-specific cuts'] },
    { id: 2, title: 'Veterinary Technician', department: 'Veterinary', location: 'Main Location', type: 'Full-time',
      description: 'Join our veterinary team to provide exceptional care for our boarding and daycare guests. CVT certification required.',
      requirements: ['CVT certification', '2+ years experience', 'Emergency care knowledge', 'Strong communication skills'] },
    { id: 3, title: 'Customer Service Representative', department: 'Customer Service', location: 'Main Location', type: 'Part-time',
      description: 'Be the friendly face that greets our clients and their pets. Perfect for someone who loves animals and people equally.',
      requirements: ['Customer service experience', 'Computer proficiency', 'Flexible schedule', 'Love for animals'] },
    { id: 4, title: 'Pet Care Attendant', department: 'Customer Service', location: 'Main Location', type: 'Full-time',
      description: 'Care for our boarding and daycare guests, ensuring they have a safe, fun, and comfortable stay.',
      requirements: ['Animal handling experience', 'Physical stamina', 'Attention to detail', 'Team player'] },
    { id: 5, title: 'Facility Manager', department: 'Management', location: 'Main Location', type: 'Full-time',
      description: 'Lead our team and oversee daily operations. Looking for someone with management experience in pet care or hospitality.',
      requirements: ['5+ years management experience', 'Pet care industry knowledge', 'Leadership skills', 'Business acumen'] },
    { id: 6, title: 'Junior Groomer', department: 'Grooming', location: 'Main Location', type: 'Full-time',
      description: "Start your grooming career with us! We provide training and mentorship for passionate individuals.",
      requirements: ['Grooming school graduate or 1+ year experience', 'Willingness to learn', 'Patient with animals', 'Good physical condition'] }
  ];

  const filteredPositions = selectedDepartment === 'All'
    ? openPositions
    : openPositions.filter(position => position.department === selectedDepartment);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E6E0F5] via-[#F0EBF8] to-[#D4C5F0]">
      <SEOHead
        title="Careers at Fluffy Pets | Pet Care Jobs San Francisco - Join Our Team"
        description="Explore pet care careers at Fluffy Pets. We're hiring groomers, veterinary technicians, trainers, and more in San Francisco. Great benefits, flexible hours, and a team that loves animals."
        keywords="pet care jobs San Francisco, pet groomer jobs, veterinary technician jobs, careers fluffy pets"
        canonical={`${SITE_URL}/careers`}
        schema={careersSchema}
      />
      {/* Navbar */}
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/ae217e5ef1f4a035b7b045b6b3280b3a.jpeg"
                alt="Fluffy Pets Logo" className="w-16 h-16 object-contain" />
            </div>
            <button onClick={() => navigate('/')} className="text-[#9B4C9A] hover:text-[#FF9BC8] transition-colors cursor-pointer flex items-center gap-2">
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-semibold">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent" style={{ fontFamily: 'Pacifico, cursive' }}>
            Join Our Team
          </h1>
          <p className="text-xl text-[#4A3B5C] max-w-3xl mx-auto leading-relaxed mb-8">
            Love animals? So do we! Join our passionate team and make a difference in the lives of pets and their families every day.
          </p>
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#9B4C9A] mb-2">50+</div>
              <div className="text-[#8B7BA8]">Team Members</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#9B4C9A] mb-2">24</div>
              <div className="text-[#8B7BA8]">Years in Business</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#9B4C9A] mb-2">4.9</div>
              <div className="text-[#8B7BA8]">Employee Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent" style={{ fontFamily: 'Pacifico, cursive' }}>
            Why Work With Us
          </h2>
          <p className="text-center text-[#8B7BA8] text-lg mb-16 max-w-2xl mx-auto">
            We believe in taking care of our team so they can take care of our furry guests
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#4A3B5C] mb-3">{benefit.title}</h3>
                <p className="text-[#8B7BA8] leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] bg-clip-text text-transparent" style={{ fontFamily: 'Pacifico, cursive' }}>
            Open Positions
          </h2>
          <p className="text-center text-[#8B7BA8] text-lg mb-12 max-w-2xl mx-auto">
            Find your perfect role and start making a difference today
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {departments.map((dept) => (
              <button key={dept} onClick={() => setSelectedDepartment(dept)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedDepartment === dept
                    ? 'bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white shadow-lg'
                    : 'bg-white text-[#9B4C9A] hover:shadow-md'
                }`}>
                {dept}
              </button>
            ))}
          </div>
          <div className="space-y-6">
            {filteredPositions.map((position) => (
              <div key={position.id} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-3xl font-bold text-[#4A3B5C]">{position.title}</h3>
                      <span className="px-4 py-1 bg-gradient-to-r from-[#9B4C9A]/10 to-[#FF9BC8]/10 text-[#9B4C9A] rounded-full text-sm font-semibold">
                        {position.department}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 mb-4 text-[#8B7BA8]">
                      <div className="flex items-center gap-2"><i className="ri-map-pin-line"></i><span>{position.location}</span></div>
                      <div className="flex items-center gap-2"><i className="ri-time-line"></i><span>{position.type}</span></div>
                    </div>
                    <p className="text-[#8B7BA8] leading-relaxed mb-4">{position.description}</p>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-[#4A3B5C]">Requirements:</h4>
                      <ul className="space-y-1">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[#8B7BA8]">
                            <i className="ri-checkbox-circle-fill text-[#9B4C9A] mt-1"></i>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <button onClick={() => navigate('/contact')}
                    className="bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer">
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] rounded-3xl p-12 text-center text-white shadow-2xl">
          <i className="ri-question-line text-6xl mb-6"></i>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Pacifico, cursive' }}>
            Don&apos;t See Your Role?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            We&apos;re always looking for talented, passionate people. Send us your resume and let&apos;s talk!
          </p>
          <button onClick={() => navigate('/contact')}
            className="bg-white text-[#9B4C9A] px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer">
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
}
